import { COUNTRY_CODE } from '../constants';

/**
 * Maps ISO country codes to country calling codes
 */
const ISO_TO_CALLING_CODE: Record<string, string> = {
  'QA': '+974', // Qatar
  'US': '+1',
  'GB': '+44',
  'SA': '+966', // Saudi Arabia
  'AE': '+971', // UAE
  'KW': '+965', // Kuwait
  'OM': '+968', // Oman
  'BH': '+973', // Bahrain
  'EG': '+20', // Egypt
  'JO': '+962', // Jordan
  'LB': '+961', // Lebanon
  'IN': '+91', // India
  'PK': '+92', // Pakistan
  'BD': '+880', // Bangladesh
  'PH': '+63', // Philippines
  'ID': '+62', // Indonesia
  'MY': '+60', // Malaysia
  'SG': '+65', // Singapore
  'TH': '+66', // Thailand
  'VN': '+84', // Vietnam
  'CN': '+86', // China
  'JP': '+81', // Japan
  'KR': '+82', // South Korea
  'AU': '+61', // Australia
  'NZ': '+64', // New Zealand
  'CA': '+1',
  'MX': '+52', // Mexico
  'BR': '+55', // Brazil
  'AR': '+54', // Argentina
  'CL': '+56', // Chile
  'CO': '+57', // Colombia
  'PE': '+51', // Peru
  'ZA': '+27', // South Africa
  'NG': '+234', // Nigeria
  'KE': '+254', // Kenya
  'GH': '+233', // Ghana
  'MA': '+212', // Morocco
  'DZ': '+213', // Algeria
  'TN': '+216', // Tunisia
  'TR': '+90', // Turkey
  'IR': '+98', // Iran
  'IQ': '+964', // Iraq
  'IL': '+972', // Israel
  'PS': '+970', // Palestine
  'YE': '+967', // Yemen
  'SY': '+963', // Syria
  'FR': '+33', // France
  'DE': '+49', // Germany
  'IT': '+39', // Italy
  'ES': '+34', // Spain
  'PT': '+351', // Portugal
  'NL': '+31', // Netherlands
  'BE': '+32', // Belgium
  'CH': '+41', // Switzerland
  'AT': '+43', // Austria
  'SE': '+46', // Sweden
  'NO': '+47', // Norway
  'DK': '+45', // Denmark
  'FI': '+358', // Finland
  'PL': '+48', // Poland
  'RU': '+7', // Russia
  'UA': '+380', // Ukraine
  'GR': '+30', // Greece
  'RO': '+40', // Romania
  'BG': '+359', // Bulgaria
  'CZ': '+420', // Czech Republic
  'HU': '+36', // Hungary
  'IE': '+353', // Ireland
  'IS': '+354', // Iceland
  'LU': '+352', // Luxembourg
  'MT': '+356', // Malta
  'CY': '+357', // Cyprus
};

/**
 * Detects user's country based on browser timezone only
 * Returns an object with both the calling code and ISO country code
 */
export const detectUserCountry = (): { callingCode: string; isoCode?: string } => {
  try {
    // Get country from timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Extract country from timezone (e.g., "Asia/Qatar" -> "QA")
    // Common timezone patterns
    const timezoneToCountry: Record<string, string> = {
      'Asia/Qatar': 'QA',
      'Asia/Dubai': 'AE',
      'Asia/Riyadh': 'SA',
      'Asia/Kuwait': 'KW',
      'Asia/Muscat': 'OM',
      'Asia/Bahrain': 'BH',
      'America/New_York': 'US',
      'America/Los_Angeles': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'Europe/London': 'GB',
      'Asia/Kolkata': 'IN',
      'Asia/Calcutta': 'IN', // Alternative name for Kolkata
      'Asia/Karachi': 'PK',
      'Asia/Dhaka': 'BD',
      'Asia/Manila': 'PH',
      'Asia/Jakarta': 'ID',
      'Asia/Kuala_Lumpur': 'MY',
      'Asia/Singapore': 'SG',
      'Asia/Bangkok': 'TH',
      'Asia/Ho_Chi_Minh': 'VN',
      'Asia/Shanghai': 'CN',
      'Asia/Tokyo': 'JP',
      'Asia/Seoul': 'KR',
      'Australia/Sydney': 'AU',
      'Pacific/Auckland': 'NZ',
      'America/Toronto': 'CA',
      'America/Mexico_City': 'MX',
      'America/Sao_Paulo': 'BR',
      'America/Buenos_Aires': 'AR',
      'America/Santiago': 'CL',
      'America/Bogota': 'CO',
      'America/Lima': 'PE',
      'Africa/Johannesburg': 'ZA',
      'Africa/Lagos': 'NG',
      'Africa/Nairobi': 'KE',
      'Africa/Accra': 'GH',
      'Africa/Casablanca': 'MA',
      'Africa/Algiers': 'DZ',
      'Africa/Tunis': 'TN',
      'Europe/Istanbul': 'TR',
      'Asia/Tehran': 'IR',
      'Asia/Baghdad': 'IQ',
      'Asia/Jerusalem': 'IL',
      'Asia/Gaza': 'PS',
      'Asia/Aden': 'YE',
      'Asia/Damascus': 'SY',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Europe/Rome': 'IT',
      'Europe/Madrid': 'ES',
      'Europe/Lisbon': 'PT',
      'Europe/Amsterdam': 'NL',
      'Europe/Brussels': 'BE',
      'Europe/Zurich': 'CH',
      'Europe/Vienna': 'AT',
      'Europe/Stockholm': 'SE',
      'Europe/Oslo': 'NO',
      'Europe/Copenhagen': 'DK',
      'Europe/Helsinki': 'FI',
      'Europe/Warsaw': 'PL',
      'Europe/Moscow': 'RU',
      'Europe/Kiev': 'UA',
      'Europe/Athens': 'GR',
      'Europe/Bucharest': 'RO',
      'Europe/Sofia': 'BG',
      'Europe/Prague': 'CZ',
      'Europe/Budapest': 'HU',
      'Europe/Dublin': 'IE',
      'Atlantic/Reykjavik': 'IS',
      'Europe/Luxembourg': 'LU',
      'Europe/Malta': 'MT',
      'Asia/Nicosia': 'CY',
    };

    if (timezoneToCountry[timezone]) {
      const isoCode = timezoneToCountry[timezone];
      const callingCode = ISO_TO_CALLING_CODE[isoCode];
      if (callingCode) {
        return { callingCode, isoCode };
      }
    }
  } catch (error) {
    // Silently handle errors
  }

  // Default fallback to Qatar (+974)
  return { callingCode: '+974', isoCode: 'QA' };
};

/**
 * Detects user's country based on browser locale and timezone
 * Returns the country calling code (e.g., '+974' for Qatar)
 * @deprecated Use detectUserCountry() instead for better accuracy
 */
export const detectUserCountryCode = (): string => {
  return detectUserCountry().callingCode;
};

/**
 * Finds the country calling code from COUNTRY_CODE array
 * If isoCode is provided, it will prefer that country when multiple countries share the same calling code
 * Returns the calling code if found, otherwise returns the default
 */
export const findCountryCallingCode = (callingCode: string, isoCode?: string): string => {
  // Normalize the calling code (remove spaces)
  const normalizedCode = callingCode.replace(/\s/g, '');
  
  // If we have an ISO code, try to find the country that matches both the calling code AND the ISO code
  if (isoCode) {
    // First, try to find a country that matches the calling code and has a name that suggests the ISO code
    // Since COUNTRY_CODE doesn't have ISO codes directly, we'll use a mapping
    const isoToCountryName: Record<string, string> = {
      'US': 'United States',
      'CA': 'Canada',
      'GB': 'United Kingdom',
      'QA': 'Qatar',
      'SA': 'Saudi Arabia',
      'AE': 'United Arab Emirates',
      'KW': 'Kuwait',
      'OM': 'Oman',
      'BH': 'Bahrain',
      // Add more as needed
    };
    
    const preferredCountryName = isoToCountryName[isoCode];
    if (preferredCountryName) {
      const preferredCountry = COUNTRY_CODE.find(
        (c) => c.code.replace(/\s/g, '') === normalizedCode && c.name === preferredCountryName
      );
      if (preferredCountry) {
        return preferredCountry.code.trim();
      }
    }
  }
  
  // Fallback: Find any exact match in COUNTRY_CODE
  const country = COUNTRY_CODE.find(
    (c) => c.code.replace(/\s/g, '') === normalizedCode
  );
  
  if (country) {
    return country.code.trim();
  }
  
  // If not found, return the detected code or default
  return normalizedCode || '+974';
};

